import { FC, useEffect, useMemo, useCallback } from 'react';
import { Navigate } from 'react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import * as config from 'config';

import {
  authSelector,
  validateToken,
  authStart,
  localizationSelector,
  setLocale,
} from 'store';

import logoGroup from 'assets/vectors/logo-group.svg';

import { useAppDispatch, useAppSelector } from 'hooks/redux';
import * as routes from 'router/Routes';
import { showErrorToast } from 'utils/toast';
import { useQuery } from 'hooks/useQuery';
import { Language } from 'models';

import Button from 'components/UI/Button';
import Loader from 'components/UI/Loader';
import Heading from 'components/UI/Heading';
import NavDropdownButton from 'components/Layout/NavBar/NavDropdownButton';

import {
  PageContainer,
  ImageSection,
  ImageContent,
  LoginSection,
  LoginContent,
  LoginDescription,
  ButtonGrid,
  LogoGroup,
  LogoGroupImage,
  LogoGroupText,
  LanguageContainer,
} from './styled';

const Login: FC = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { locale, languages } = useAppSelector(localizationSelector);
  const {
    session: { isAuthenticated },
    isLoading,
  } = useAppSelector(authSelector);
  const [refreshToken, accessToken, error, errorMessage] = useQuery(
    'refresh_token',
    'access_token',
    'error',
    'error_description'
  );

  // Validate token
  useEffect(() => {
    if (accessToken) {
      dispatch(validateToken(accessToken, refreshToken || ''));
    }
  }, [dispatch, accessToken, refreshToken]);

  // Show error
  useEffect(() => {
    if (error) {
      // Message
      const showMessage = (): string => {
        if (error === 'interaction_required') {
          return `${intl.formatMessage({ id: 'login.unauthorized' })}`;
        }
        return `${errorMessage}`;
      };
      showErrorToast(showMessage());
    }
  }, [dispatch, error, errorMessage, intl]);

  // Azure login
  const onLogin = useCallback(() => {
    console.log(`onlogin ${config.backendApi}/isim/login`);
    dispatch(authStart());
    window.location.assign(`${config.backendApi}/isim/login`);
  }, [dispatch]);

  // Language menu
  const langMenu = languages.map((language: Language, i: number) => ({
    id: i + 1,
    text: language.toUpperCase(),
    onClick: () => dispatch(setLocale(language)),
  }));

  // Render content
  const content = useMemo(() => {
    if (isLoading) {
      return <Loader center size="medium" />;
    }
    return (
      <ButtonGrid>
        <Button onClick={onLogin}>
          <FormattedMessage id="login.btn_isim" />
        </Button>
      </ButtonGrid>
    );
  }, [onLogin, isLoading]);

  // Authenticated
  if (isAuthenticated) {
    const redirectTo = localStorage.getItem('redirectTo');
    const navigateTo =
      !redirectTo || redirectTo.startsWith('/login') ? routes.ROOT : redirectTo;
    return <Navigate to={navigateTo} />;
  }

  return (
    <PageContainer>
      <ImageSection>
        <ImageContent>
          <Heading>
            <FormattedMessage id="login.image_title" />
          </Heading>
          <LoginDescription>
            <FormattedMessage id="login.image_description" />
          </LoginDescription>
        </ImageContent>
      </ImageSection>
      <LoginSection>
        <LanguageContainer>
          <NavDropdownButton text={locale.toUpperCase()} menu={langMenu} />
        </LanguageContainer>
        <LoginContent>
          <Heading>
            <FormattedMessage id="login.title" />
          </Heading>
          <LoginDescription>
            <FormattedMessage id="login.description" />
          </LoginDescription>
          {content}
          <LogoGroup>
            <LogoGroupImage src={logoGroup} alt="husqvarna group logo" />
            <LogoGroupText>
              <FormattedMessage id="login.group_text" />
            </LogoGroupText>
          </LogoGroup>
        </LoginContent>
      </LoginSection>
    </PageContainer>
  );
};

export default Login;
